import React, { useRef, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Download, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";

const depthColor = (d) => (d < 50 ? "#ef4444" : d <= 300 ? "#f97316" : "#10b981");

// Load a 3x3 grid of OSM tiles around a lat/lng and stitch onto a canvas rect.
async function loadTileMosaic(lat, lng, zoom = 5) {
  const n = Math.pow(2, zoom);
  const xFrac = (lng + 180) / 360 * n;
  const latRad = lat * Math.PI / 180;
  const yFrac = (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n;
  const cx = Math.floor(xFrac);
  const cy = Math.floor(yFrac);
  const ox = xFrac - cx;
  const oy = yFrac - cy;
  const size = 256;
  const grid = 3;
  const canvas = document.createElement("canvas");
  canvas.width = size * grid;
  canvas.height = size * grid;
  const ctx = canvas.getContext("2d");

  const loadImg = (url) => new Promise((res) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => res(im);
    im.onerror = () => res(null);
    im.src = url;
  });

  const jobs = [];
  for (let iy = -1; iy <= 1; iy++) {
    for (let ix = -1; ix <= 1; ix++) {
      const tx = ((cx + ix) % n + n) % n;
      const ty = cy + iy;
      if (ty < 0 || ty >= n) continue;
      const url = `https://tile.openstreetmap.org/${zoom}/${tx}/${ty}.png`;
      jobs.push(loadImg(url).then((im) => {
        if (im) ctx.drawImage(im, (ix + 1) * size, (iy + 1) * size, size, size);
      }));
    }
  }
  await Promise.all(jobs);
  const centerX = size + ox * size;
  const centerY = size + oy * size;
  return { canvas, centerX, centerY, size: size * grid };
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrapText(ctx, text, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let cur = "";
  words.forEach((w) => {
    const test = cur ? cur + " " + w : w;
    if (ctx.measureText(test).width > maxWidth) { lines.push(cur); cur = w; }
    else cur = test;
  });
  if (cur) lines.push(cur);
  return lines;
}

async function drawCard(canvas, quake, cfg) {
  const W = 1080, H = 1440;
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background base
  ctx.fillStyle = "#020617";
  ctx.fillRect(0, 0, W, H);

  // Subtle grid
  ctx.strokeStyle = "rgba(51,65,85,0.28)";
  ctx.lineWidth = 1;
  for (let x = 0; x < W; x += 40) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
  for (let y = 0; y < H; y += 40) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

  const color = depthColor(quake.depth);

  // ------ Header
  const brand = cfg.siteName + (cfg.siteSuffix ? " " + cfg.siteSuffix : "");
  ctx.fillStyle = color;
  ctx.font = "700 22px 'Plus Jakarta Sans', sans-serif";
  ctx.fillText(`● LIVE — ${quake.source || "USGS"}`, 60, 90);

  ctx.fillStyle = "#f8fafc";
  ctx.font = "800 68px 'Plus Jakarta Sans', sans-serif";
  ctx.fillText(brand, 60, 160);

  ctx.fillStyle = "#94a3b8";
  ctx.font = "500 20px 'Plus Jakarta Sans', sans-serif";
  ctx.fillText(cfg.siteSubtitle || "Real-Time Multi-Hazard Intelligence", 60, 195);

  // ------ Map block
  const mapX = 60, mapY = 240;
  const mapW = W - 120;
  const mapH = 640;
  ctx.save();
  roundRect(ctx, mapX, mapY, mapW, mapH, 24);
  ctx.clip();

  const mosaic = await loadTileMosaic(quake.latitude, quake.longitude, 5);
  const scale = Math.max(mapW / mosaic.size, mapH / mosaic.size) * 1.05;
  const drawnW = mosaic.size * scale;
  const drawnH = mosaic.size * scale;
  const dx = mapX + mapW / 2 - mosaic.centerX * scale;
  const dy = mapY + mapH / 2 - mosaic.centerY * scale;
  ctx.drawImage(mosaic.canvas, dx, dy, drawnW, drawnH);

  // Slight dark tint overlay for contrast
  ctx.fillStyle = "rgba(2,6,23,0.32)";
  ctx.fillRect(mapX, mapY, mapW, mapH);

  // Epicenter marker + concentric rings
  const ex = mapX + mapW / 2;
  const ey = mapY + mapH / 2;
  for (let i = 3; i >= 1; i--) {
    ctx.beginPath();
    ctx.arc(ex, ey, 18 * i, 0, Math.PI * 2);
    ctx.fillStyle = color + Math.round(30 - i * 6).toString(16).padStart(2, "0");
    ctx.fill();
  }
  ctx.beginPath();
  ctx.arc(ex, ey, 16, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = "#fff";
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.restore();

  // Map border
  ctx.strokeStyle = "rgba(148,163,184,0.35)";
  ctx.lineWidth = 1.5;
  roundRect(ctx, mapX, mapY, mapW, mapH, 24);
  ctx.stroke();

  // ------ Magnitude badge (overlaps map bottom-left)
  const bx = 90, by = mapY + mapH - 100;
  ctx.fillStyle = color;
  roundRect(ctx, bx, by, 260, 130, 20);
  ctx.fill();
  ctx.fillStyle = "#020617";
  ctx.font = "800 90px 'JetBrains Mono', monospace";
  ctx.fillText(`M ${quake.magnitude?.toFixed(1) ?? "-"}`, bx + 24, by + 96);

  // ------ Body: Location + stats
  let cy = mapY + mapH + 60;
  ctx.fillStyle = "#f8fafc";
  ctx.font = "700 42px 'Plus Jakarta Sans', sans-serif";
  const locLines = wrapText(ctx, quake.location || "-", W - 120).slice(0, 2);
  locLines.forEach((line, i) => ctx.fillText(line, 60, cy + i * 52));
  cy += locLines.length * 52 + 30;

  // Stats row: depth · coords · time
  const stat = (label, value, x) => {
    ctx.fillStyle = "#64748b";
    ctx.font = "600 16px 'Plus Jakarta Sans', sans-serif";
    ctx.fillText(label, x, cy);
    ctx.fillStyle = "#e2e8f0";
    ctx.font = "700 26px 'JetBrains Mono', monospace";
    ctx.fillText(value, x, cy + 34);
  };
  stat("KEDALAMAN", `${quake.depth?.toFixed(1)} km`, 60);
  stat("KOORDINAT", `${quake.latitude?.toFixed(2)}, ${quake.longitude?.toFixed(2)}`, 380);
  stat("SUMBER", quake.source || "-", 780);
  const wib = new Date(quake.time).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", dateStyle: "medium", timeStyle: "short" });
  cy += 90;
  stat("WAKTU WIB", wib, 60);
  const utc = new Date(quake.time).toISOString().replace("T", " ").slice(0, 16) + " UTC";
  ctx.fillStyle = "#94a3b8";
  ctx.font = "500 18px 'JetBrains Mono', monospace";
  ctx.fillText(utc, 60, cy + 60);

  // ------ Advisory strip
  const advY = H - 190;
  ctx.fillStyle = quake.tsunami ? "rgba(220,38,38,0.18)" : "rgba(6,182,212,0.12)";
  roundRect(ctx, 60, advY, W - 120, 100, 18);
  ctx.fill();
  ctx.strokeStyle = quake.tsunami ? "rgba(248,113,113,0.55)" : "rgba(34,211,238,0.45)";
  ctx.stroke();
  ctx.fillStyle = quake.tsunami ? "#fecaca" : "#a5f3fc";
  ctx.font = "700 18px 'Plus Jakarta Sans', sans-serif";
  ctx.fillText(quake.tsunami ? "⚠ WASPADA POTENSI TSUNAMI" : "Info Resmi — BMKG & USGS", 80, advY + 34);
  ctx.fillStyle = quake.tsunami ? "#fee2e2" : "#e0f2fe";
  ctx.font = "500 18px 'Plus Jakarta Sans', sans-serif";
  const adviceText = quake.tsunami
    ? "Ikuti arahan BNPB 117 & BMKG. Segera evakuasi ke dataran tinggi jauhi pantai."
    : "Hubungi BNPB 117 untuk bantuan darurat. Ikuti update dari sumber resmi.";
  const adviceLines = wrapText(ctx, adviceText, W - 160).slice(0, 2);
  adviceLines.forEach((l, i) => ctx.fillText(l, 80, advY + 66 + i * 22));

  // ------ Footer
  ctx.fillStyle = "#475569";
  ctx.font = "600 16px 'JetBrains Mono', monospace";
  const wm = cfg.domainUrl || cfg.siteName.toLowerCase();
  ctx.fillText(wm, 60, H - 45);
  ctx.textAlign = "right";
  ctx.fillText("BNPB · BMKG · USGS · NASA", W - 60, H - 45);
  ctx.textAlign = "start";
}

export default function SocialCard({ open, onOpenChange, quake, cfg }) {
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef();

  useEffect(() => {
    if (!open || !quake) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        if (!cancelled) await drawCard(canvasRef.current, quake, cfg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, quake, cfg]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `pantau-M${quake.magnitude?.toFixed(1)}-3x4.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
    toast.success("Kartu berhasil diunduh");
  };

  const copyImage = async () => {
    try {
      const canvas = canvasRef.current;
      const blob = await new Promise((r) => canvas.toBlob(r, "image/png"));
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
      toast.success("Gambar disalin ke clipboard");
    } catch {
      toast.error("Browser tidak mendukung salin gambar. Gunakan Download.");
    }
  };

  if (!quake) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[92vh] overflow-y-auto border-2 border-slate-700 bg-slate-900 text-slate-100 shadow-2xl shadow-cyan-500/10" data-testid="social-card-modal">
        <DialogHeader>
          <DialogTitle>Social Card · 3:4 Vertikal (1080×1440)</DialogTitle>
        </DialogHeader>
        <div className="rounded-lg border border-slate-800 bg-slate-900 p-3">
          <canvas ref={canvasRef} className="mx-auto block max-h-[65vh] w-auto object-contain" data-testid="social-card-canvas" />
          {loading && (
            <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
              <Loader2 className="h-3 w-3 animate-spin" /> Menyusun kartu…
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          <Button onClick={download} disabled={loading} className="bg-cyan-500 text-slate-950 hover:bg-cyan-400" data-testid="social-card-download">
            <Download className="mr-2 h-4 w-4" /> Download PNG
          </Button>
          <Button onClick={copyImage} disabled={loading} variant="outline" className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800" data-testid="social-card-copy">
            <Copy className="mr-2 h-4 w-4" /> Salin ke Clipboard
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
